/**
 * Newsletter Email Validation
 * Validates email addresses for newsletter signup forms
 * Provides real-time validation feedback for incomplete or invalid email addresses
 */

class NewsletterValidator {
  constructor() {
    this.init();
  }

  init() {
    // Find all forms that contain newsletter email inputs
    const allForms = document.querySelectorAll("form");

    allForms.forEach((form) => {
      if (form.closest(".PopupModal-subscribe-wrapper")) return;

      const emailInput = form.querySelector('input[name="contact[email]"]');

      // Check if this is a newsletter form
      const hasNewsletterTag = form.querySelector(
        'input[name="contact[tags]"][value="newsletter"]',
      );

      if (!emailInput || !hasNewsletterTag) return;

      // Remove any existing listeners to avoid duplicates
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);

      // Get references to the new elements
      const newEmailInput = newForm.querySelector(
        'input[name="contact[email]"]',
      );

      // Add real-time validation on blur and input
      newEmailInput.addEventListener("blur", (e) =>
        this.validateEmail(e.target, newForm),
      );
      newEmailInput.addEventListener("input", (e) => {
        if (e.target.value.length > 0) {
          this.clearError(newForm);
        }
      });

      // Enhanced validation on form submit
      newForm.addEventListener(
        "submit",
        (e) => {
          if (!this.validateEmail(newEmailInput, newForm)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            newEmailInput.focus();
            return false;
          }

          // --- ADDED: handle success message auto-hide ---
          setTimeout(() => {
            this.handleSuccessMessage(newForm);
          }, 50);
        },
        true,
      );

      // --- ADDED: also check if a success message is already rendered ---
      this.handleSuccessMessage(newForm);
    });
  }

  /* -------------------------------------------------------
     NEW METHOD: Auto-hide success message after 10 seconds
  -------------------------------------------------------- */
  handleSuccessMessage(form) {
    const success = form.querySelector(".newsletter-form__message--success");

    if (!success) return;

    // Prevent multiple timers
    if (success.dataset.timed === "true") return;

    success.dataset.timed = "true";

    // Show immediately (if hidden)
    success.style.display = "block";
    success.style.opacity = "1";

    // Hide after 10 seconds
    setTimeout(() => {
      success.style.transition = "opacity 0.5s ease";
      success.style.opacity = "0";

      setTimeout(() => {
        success.style.display = "none";
      }, 500);
    }, 10000);
  }

  validateEmail(input, form) {
    const email = input.value.trim();

    this.clearError(form);

    if (email === "") {
      this.showError(form, input, window.variantStrings.enterEmail);
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      let errorMessage = window.variantStrings.enterEmail;

      if (email.indexOf("@") === -1) {
        errorMessage = window.variantStrings.emailWithSymbol;
      } else if (email.indexOf("@") === email.length - 1) {
        errorMessage = window.variantStrings.enterDomain;
      } else if (
        email.split("@")[1] &&
        email.split("@")[1].indexOf(".") === -1
      ) {
        errorMessage = window.variantStrings.domainMust;
      } else if (email.endsWith(".")) {
        errorMessage = window.variantStrings.domainExtention;
      } else if (email.startsWith("@")) {
        errorMessage = window.variantStrings.emailBeforeSymbol;
      }

      this.showError(form, input, errorMessage);
      return false;
    }

    const parts = email.split("@");
    if (parts[0].length === 0) {
      this.showError(form, input, window.variantStrings.emailBeforeSymbol);
      return false;
    }

    const domain = parts[1];
    const domainParts = domain.split(".");

    if (
      domainParts.length < 2 ||
      domainParts.some((part) => part.length === 0)
    ) {
      this.showError(form, input, window.variantStrings.domainExtention);
      return false;
    }

    if (domainParts[domainParts.length - 1].length < 2) {
      this.showError(form, input, window.variantStrings.domainExtention);
      return false;
    }

    return true;
  }

  showError(form, input, message) {
    input.setAttribute("aria-invalid", "true");
    input.classList.add("field__input--error");

    let errorContainer = form.querySelector(".newsletter-form__message--error");

    if (!errorContainer) {
      errorContainer = document.createElement("small");
      errorContainer.className =
        "newsletter-form__message newsletter-form__message--error form__message";
      errorContainer.setAttribute("role", "alert");

      const iconWrapper = document.createElement("span");
      iconWrapper.className = "svg-wrapper";
      iconWrapper.innerHTML = `<svg aria-hidden="true" width="20px" height="20px" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
        <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"/>
        <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"/>
      </svg>`;
      errorContainer.appendChild(iconWrapper);

      const messageSpan = document.createElement("span");
      messageSpan.className = "newsletter-form__message-text";
      errorContainer.appendChild(messageSpan);

      const fieldWrapper = input.closest(
        ".newsletter-form__field-wrapper, .field",
      );
      if (fieldWrapper && fieldWrapper.parentNode) {
        fieldWrapper.parentNode.insertBefore(
          errorContainer,
          fieldWrapper.nextSibling,
        );
      } else {
        input.parentNode.insertBefore(errorContainer, input.nextSibling);
      }
    }

    const messageText = errorContainer.querySelector(
      ".newsletter-form__message-text",
    );
    if (messageText) {
      messageText.textContent = message;
    }

    errorContainer.style.display = "block";
  }

  clearError(form) {
    const input = form.querySelector('input[type="email"]');
    if (input) {
      input.removeAttribute("aria-invalid");
      input.classList.remove("field__input--error");
    }

    const errorContainer = form.querySelector(
      ".newsletter-form__message--error",
    );
    if (errorContainer) {
      errorContainer.style.display = "none";
    }
  }
}

// Initialize validator when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new NewsletterValidator();
  });
} else {
  new NewsletterValidator();
}

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    new NewsletterValidator();
  });
}
